import React from "react";
import { FiTrash2, FiEdit2 } from "react-icons/fi";

export type QuoteItem = {
    id: string;
    name: string;
    qty: number;
    amount: number; // total line amount (not per-item)
    provider?: string | '';
};

type ItemLine = {
    id: string;
    provider?: string | '';
    name?: string | '';
    qty?: number | string | '';
    costPerItem?: number | string | '';
    profitPerItem?: number | string | '';
    pricePerItem?: number | string | '';
    lineTotal?: number | string | '';
};

type Props = {
    items: QuoteItem[];
    onAddClick: () => void;
    onEdit: (id: string) => void;
    onCancelEdit: () => void;
    onRemove: (id: string) => void;
    currency?: string; // default USD
    className?: string;
    editIndex?: number | null;
    itemLine?: ItemLine | null;
};

const fmt = (n: number, currency = "USD") =>
    n.toLocaleString(undefined, { style: "currency", currency });

export default function ItemsList({
    items,
    onAddClick,
    onEdit,
    onRemove,
    currency = "USD",
    className = "",
    editIndex,
    onCancelEdit,
    itemLine
}: Props) {
    const isValidLineItem = Boolean(itemLine?.provider?.trim() && itemLine?.name?.trim() && String(itemLine?.qty).trim());

    return (
        <div className={className}>
            {/* Save & Add (dashed pill) */}
            {editIndex !== null ? (
                <div className="flex flex-row gap-3">
                    <button
                        type="button"
                        disabled={!isValidLineItem}
                        onClick={onAddClick}
                        className="w-full rounded-2xl font-[400] border border-dashed border-[#698293] bg-[#F7F8FA] py-3 text-xs text-[#282828] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#F7F8FA]"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={onCancelEdit}
                        className="w-full rounded-2xl font-[400] border border-dashed border-[#698293] bg-[#F7F8FA] py-3 text-xs text-[#282828] hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <button
                    disabled={!isValidLineItem}
                    type="button"
                    onClick={onAddClick}
                    className="w-full rounded-2xl font-[400] border border-dashed border-[#698293] bg-[#F7F8FA] py-3 text-xs text-[#282828] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#F7F8FA]"
                >
                    + Save and Add
                </button>
            )}

            {/* Items */}
            <ul className="mt-3">
                {items.map((it, index) => (
                    <li
                        key={it.id}
                        className={`flex items-center border-b gap-3 px-4 py-4 ${index == 0 && "border-t"
                            }`}
                    >
                        {/* Name */}
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-gray-900">
                                {it.name}
                            </p>
                        </div>
                        {it.provider !== 'customerProvide' && (
                            <div className="min-w-0 flex-1">
                                <p className="mt-0.5 text-xs text-gray-400">Qty: {it.qty}</p>
                            </div>
                        )}
                        {/* Amount - hidden for client provide */}
                        {it.provider !== 'customerProvide' && (
                            <div className="text-right">
                                <p className="text-sm font-semibold text-gray-900">
                                    {fmt(it.amount, currency)}
                                </p>
                            </div>
                        )}
                        {/* Actions */}
                        <div className="ml-2 inline-flex items-center gap-2 text-gray-400">
                            <button
                                type="button"
                                onClick={() => onRemove(it.id)}
                                className="rounded-full p-[5px] text-rose-500 bg-gray-100"
                                aria-label={`Remove ${it.name}`}
                            >
                                <FiTrash2 className="h-3 w-3" />
                            </button>
                            <button
                                type="button"
                                onClick={() => onEdit(it.id)}
                                className="rounded-full p-[5px] hover:text-gray-700 bg-gray-100"
                                aria-label={`Edit ${it.name}`}
                            >
                                <FiEdit2 className="h-3 w-3" />
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}
